/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { apiEndpoints, httpMethods } from "../../../constants/api.constants";
import useAPI from "../../../hooks/useAPI";
import useI18N from "../../../hooks/useI18N";
import { Logo } from "../../../shared/icon";
import { MUIStyled } from "../../../shared/MUIStyled";
import SABox from "../../../shared/SABox";
import SAButton from "../../../shared/SAButton";
import SALoader from "../../../shared/SALoader";
import SAStack from "../../../shared/SAStack";
import SATypography from "../../../shared/SATypography";
import TapCard from "./RenderTapCard";
import SubscriptionMessageModal from "./SubscriptionMessageModal";

const AUTHORIZE_AMOUNT = 0.1;
const AUTHORIZE_CURRENCY = "BHD";

const RegistrationWrapper = MUIStyled(SAStack, {
  shouldForwardProp: (prop) => prop !== "step",
})(({ theme }) => ({
  paddingTop: "0px !important",
  padding: 20,
  minHeight: "100vh",
  backgroundColor: "#111",
  [theme.breakpoints.down("sm")]: {
    background: `linear-gradient(to bottom, #111 240px, ${theme.palette.white.main} 15%)`,
  },
}));

const GOLD_COLOR = "#C5A56F";

const StyledTitle = MUIStyled(SATypography)(({ theme, dir }) => ({
  fontSize: 24,
  marginTop: theme.spacing(2),
  fontWeight: 700,
  textAlign: dir === "rtl" ? "right" : "left",
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: { fontSize: 20, fontWeight: 600 },
}));

const StyledSubtitle = MUIStyled(SATypography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: GOLD_COLOR,
  marginBottom: theme.spacing(3),
  background: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  letterSpacing: "0.2px",
  border: `1px solid ${GOLD_COLOR}`,
  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
    padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  },
}));

const StyledPayButton = MUIStyled(SAButton)(({ theme }) => ({
  borderRadius: 0,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const StyledProcessingContainer = MUIStyled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  textAlign: "center",
  minHeight: "400px",
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
}));

const StyledProcessingTitle = MUIStyled(SATypography)(({ theme }) => ({
  color: "#a78a58",
  fontWeight: 600,
  fontSize: 28,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: { fontSize: 24 },
}));

const StyledLoadingSpinner = MUIStyled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  margin: theme.spacing(3, 0),
  "& span": {
    width: 12,
    height: 12,
    backgroundColor: "#a78a58",
    borderRadius: "50%",
    display: "inline-block",
    animation: "pulse 1.4s infinite ease-in-out",
    "&:nth-of-type(1)": { animationDelay: "0s" },
    "&:nth-of-type(2)": { animationDelay: "0.2s" },
    "&:nth-of-type(3)": { animationDelay: "0.4s" },
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 0.6, transform: "scale(0.6)" },
    "50%": { opacity: 1, transform: "scale(1)" },
  },
}));

const StyledProcessingSubtitle = MUIStyled(SATypography)(({ theme }) => ({
  fontSize: 16,
  maxWidth: "400px",
  lineHeight: 1.5,
  marginTop: theme.spacing(3),
  color: theme.palette.text.secondary,
}));

const AddTapCardDetails = () => {
  const { api } = useAPI();
  const { t, dir } = useI18N();
  const navigate = useNavigate();

  const [isCardReady, setIsCardReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isSuccessResponse, setIsSuccessResponse] = useState(false);
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] =
    useState(null);

  const loaders = useSelector((state) => ({
    updateCard: state.loader?.["update-tap-card"],
  }));

  // Extract access token from URL or sessionStorage
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get("t");
  const refreshToken = urlParams.get("refresh_token");
  if (urlToken || refreshToken) {
    sessionStorage.setItem("refreshToken_addTapCard", refreshToken);
    sessionStorage.setItem("accessToken_addTapCard", urlToken);
  }
  const storedToken =
    urlToken || sessionStorage.getItem("accessToken_addTapCard");

  const deepLink = `sooq://onboard-complete?access_token=${storedToken}`;
  const fallbackUrl = `/onboard-complete?access_token=${storedToken}`;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Detect tap_id in URL (redirect back from 3DS)
  const tapAuthorizeId =
    urlParams.get("tap_id") ||
    new URLSearchParams(window.location.search).get("tap_id");

  const isProcessing = isSubmitting || isRedirecting || !!tapAuthorizeId;

  const showSuccess = () => {
    setIsErrorMessage(false);
    setIsSuccessResponse(true);
    setSubscriptionModalOpen(true);
  };

  const showError = (msg) => {
    setIsSuccessResponse(false);
    setIsErrorMessage(true);
    setSubscriptionErrorMessage(msg || "card-details-generic-error-message");
    setSubscriptionModalOpen(true);
  };

  // On mount: if tap_id is present, send it to backend
  useEffect(() => {
    if (tapAuthorizeId) {
      sendAuthorizeIdToBackend(tapAuthorizeId);
    }
  }, []);

  // Send authorize_id to backend to save the card
  const sendAuthorizeIdToBackend = async (authorizeId) => {
    const token = urlToken || sessionStorage.getItem("accessToken_addTapCard");

    const response = await api({
      method: httpMethods.post,
      endPoint: apiEndpoints.updateUserCardDetails,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      data: {
        tap_authorize_id: authorizeId,
      },
      needLoader: true,
      loader: "update-tap-card",
      showToastOnError: false,
    });

    if (response?.status) {
      showSuccess();
    } else {
      showError(
        response?.error?.response?.data?.error?.detail?.tap_authorize_id ||
          "failed-save-card-details",
      );
    }

    // Clean up URL params
    clearUrlParams();
  };

  // Handle token received from TAP Card SDK
  // Memoized to prevent RenderTapCard useEffect from re-running (it depends on onToken)
  const handleTokenReceived = useCallback(
    async (token) => {
      if (!token) return;

      setIsSubmitting(true);

      const response = await api({
        method: httpMethods.post,
        endPoint: apiEndpoints.tapCreateAuthorize,
        headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {},
        data: {
          card_token: token,
        },
        needLoader: true,
        loader: "initiate-tap-authorize",
      });

      setIsSubmitting(false);

      if (response?.status && response?.data?.redirect_url) {
        if (response?.data?.authorize_id) {
          sessionStorage.setItem(
            "tap_authorize_id",
            response.data.authorize_id,
          );
        }
        setIsRedirecting(true);
        window.location.replace(response.data.redirect_url);
      } else if (response?.status && response?.data?.authorize_id) {
        // No 3DS needed, card authorized directly
        await sendAuthorizeIdToBackend(response.data.authorize_id);
      } else {
        showError(
          response?.error?.response?.data?.error?.detail ||
            "card-details-generic-error-message",
        );
      }
    },
    [storedToken],
  );

  // Trigger tokenization via TAP Card SDK
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (window.CardSDK && typeof window.CardSDK.tokenize === "function") {
      window.CardSDK.tokenize();
    } else {
      console.error("Tap CardSDK not loaded");
      setIsSubmitting(false);
    }
  };

  const clearUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("tap_id");
    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");
    window.history.replaceState({}, "", newUrl);
  };

  const handleClose = () => {
    if (isSuccessResponse) {
      if (isMobile) {
        window.location.href = deepLink || fallbackUrl;
      } else {
        navigate("/onboard-complete");
      }
      sessionStorage.removeItem("accessToken_addTapCard");
      return;
    }

    // On error close, reload to reset state
    const params = new URLSearchParams(window.location.search);
    params.delete("tap_id");
    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");

    window.location.replace(newUrl);
    // window.history.replaceState({}, "", newUrl);
    // window.location.reload();
  };

  // Memoize session object so RenderTapCard doesn't re-render on every parent render
  const tapCardSession = useMemo(
    () => ({
      email: "",
      phone_country_code: "",
      phone_number: "",
    }),
    [],
  );

  // Memoize onError callback for the same reason
  const handleCardError = useCallback(() => {
    showError("card-details-generic-error-message");
  }, []);

  return (
    <>
      <RegistrationWrapper
        alignItems="center"
        justifyContent={{ sm: "center" }}
      >
        <SABox mb={3} mt={{ xs: 5, sm: 2 }} textAlign="center">
          <Logo />
        </SABox>
        <SAStack
          width="100%"
          sx={{ "&:has(.language)": { flex: "none" } }}
          maxWidth={{
            sm: 600,
            xs: "100%",
            padding: "14px",
            backgroundColor: "#F9FAFB",
            border: "1px solid #D2D5DA",
          }}
        >
          <SABox sx={{ width: "100%" }}>
            {isProcessing || loaders?.updateCard ? (
              <StyledProcessingContainer>
                <StyledProcessingTitle>
                  {t("saving-card-details-title")}
                </StyledProcessingTitle>
                <StyledLoadingSpinner>
                  <span></span>
                  <span></span>
                  <span></span>
                </StyledLoadingSpinner>
                <StyledProcessingSubtitle>
                  {t("authorizing-card-subtitle")}
                </StyledProcessingSubtitle>
              </StyledProcessingContainer>
            ) : !isCardReady ? (
              <StyledProcessingContainer>
                <SALoader sx={{ display: "flex", justifyContent: "center" }} />
              </StyledProcessingContainer>
            ) : null}

            {/* Card entry form — only visible when SDK is ready and not processing */}
            {!tapAuthorizeId && (
              <SABox
                sx={{
                  display:
                    !isProcessing && !loaders?.updateCard && isCardReady
                      ? "block"
                      : "none",
                }}
              >
                <StyledTitle dir={dir} variant="h6">
                  {t("card-details")}
                </StyledTitle>
                <StyledSubtitle>
                  {t("tap-card-addition-subtitle")}
                </StyledSubtitle>

                <TapCard
                  onToken={handleTokenReceived}
                  onError={handleCardError}
                  session={tapCardSession}
                  amount={AUTHORIZE_AMOUNT}
                  currency={AUTHORIZE_CURRENCY}
                  onReady={() => setIsCardReady(true)}
                />

                <StyledPayButton
                  fullWidth
                  onClick={handleSubmit}
                  variant="contained"
                  id="tap-save-card"
                  loading={isSubmitting}
                >
                  {t("save-card-details")}
                </StyledPayButton>
              </SABox>
            )}
          </SABox>
        </SAStack>
      </RegistrationWrapper>

      <SubscriptionMessageModal
        subscriptionModalOpen={subscriptionModalOpen}
        isSuccesResponse={isSuccessResponse}
        isErrorMessage={isErrorMessage}
        subscriptionErrorMessage={subscriptionErrorMessage}
        successTitle="card-details-success-message"
        successSubtitle="card-details-success-subtitle"
        errorTitle="card-details-error-message-title"
        onAfterPayment={handleClose}
        goToFinalStep={handleClose}
        session={null}
      />
    </>
  );
};

export default AddTapCardDetails;
